/**
 * Generated by orval v8.11.0 🍺
 * Do not edit manually.
 * Core API
 * Core API Documentation
 * OpenAPI spec version: 1.0
 */
import { useMutation } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";

import type {
  AcknowledgeCheckFailureAlertResponseDto,
  BackfillCheckFailureAlertsDto,
  BackfillCheckFailureAlertsResponseDto,
} from "../model";

import { axiosClient } from "../../lib/api";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Records acknowledgedBy/acknowledgedAt for a failed emergency lighting test or fire extinguisher check, once the thing that failed has passed a newer check. Clears the alert from the Monitoring alerts tab and returns the dashboard Monitoring row to green. Idempotency: acknowledging an already-acknowledged alert returns 200 with the existing acknowledgedAt.
 * @summary Manager acknowledges an open failed-check alert
 */
export const acknowledgeCheckFailureAlertCommandControllerAcknowledge = (
  alertId: string,
  signal?: AbortSignal,
) => {
  return axiosClient<AcknowledgeCheckFailureAlertResponseDto>({
    url: `/v1/check-failure-alerts/${alertId}/acknowledge`,
    method: "POST",
    signal,
  });
};

export const getAcknowledgeCheckFailureAlertCommandControllerAcknowledgeMutationOptions =
  <TError = void, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof acknowledgeCheckFailureAlertCommandControllerAcknowledge
        >
      >,
      TError,
      { alertId: string },
      TContext
    >;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof acknowledgeCheckFailureAlertCommandControllerAcknowledge
      >
    >,
    TError,
    { alertId: string },
    TContext
  > => {
    const mutationKey = [
      "acknowledgeCheckFailureAlertCommandControllerAcknowledge",
    ];
    const { mutation: mutationOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey } };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof acknowledgeCheckFailureAlertCommandControllerAcknowledge
        >
      >,
      { alertId: string }
    > = (props) => {
      const { alertId } = props ?? {};

      return acknowledgeCheckFailureAlertCommandControllerAcknowledge(alertId);
    };

    return { mutationFn, ...mutationOptions };
  };

export type AcknowledgeCheckFailureAlertCommandControllerAcknowledgeMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof acknowledgeCheckFailureAlertCommandControllerAcknowledge
      >
    >
  >;

export type AcknowledgeCheckFailureAlertCommandControllerAcknowledgeMutationError =
  void;

/**
 * @summary Manager acknowledges an open failed-check alert
 */
export const useAcknowledgeCheckFailureAlertCommandControllerAcknowledge = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof acknowledgeCheckFailureAlertCommandControllerAcknowledge
        >
      >,
      TError,
      { alertId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<typeof acknowledgeCheckFailureAlertCommandControllerAcknowledge>
  >,
  TError,
  { alertId: string },
  TContext
> => {
  return useMutation(
    getAcknowledgeCheckFailureAlertCommandControllerAcknowledgeMutationOptions(
      options,
    ),
    queryClient,
  );
};
/**
 * One-off, idempotent endpoint that opens alerts for failures recorded before NAU-359/NAU-360 — failed emergency lighting tests and failed fire extinguisher check items. Run once after deploying. Backfilled alerts land ACTIVE, so nurseries with historic unacknowledged failures will show an amber Monitoring row until a manager acknowledges them. Pass `from` to limit how far back it reaches.
 * @summary Backfill failed-check alerts (platform admin only)
 */
export const backfillCheckFailureAlertsCommandControllerBackfill = (
  backfillCheckFailureAlertsDto?: BackfillCheckFailureAlertsDto,
  signal?: AbortSignal,
) => {
  return axiosClient<BackfillCheckFailureAlertsResponseDto>({
    url: `/v1/check-failure-alerts/backfill`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: backfillCheckFailureAlertsDto,
    signal,
  });
};

export const getBackfillCheckFailureAlertsCommandControllerBackfillMutationOptions =
  <TError = void, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof backfillCheckFailureAlertsCommandControllerBackfill>
      >,
      TError,
      { data?: BackfillCheckFailureAlertsDto },
      TContext
    >;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof backfillCheckFailureAlertsCommandControllerBackfill>
    >,
    TError,
    { data?: BackfillCheckFailureAlertsDto },
    TContext
  > => {
    const mutationKey = ["backfillCheckFailureAlertsCommandControllerBackfill"];
    const { mutation: mutationOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey } };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof backfillCheckFailureAlertsCommandControllerBackfill>
      >,
      { data?: BackfillCheckFailureAlertsDto }
    > = (props) => {
      const { data } = props ?? {};

      return backfillCheckFailureAlertsCommandControllerBackfill(data);
    };

    return { mutationFn, ...mutationOptions };
  };

export type BackfillCheckFailureAlertsCommandControllerBackfillMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof backfillCheckFailureAlertsCommandControllerBackfill>
    >
  >;
export type BackfillCheckFailureAlertsCommandControllerBackfillMutationBody =
  | BackfillCheckFailureAlertsDto
  | undefined;
export type BackfillCheckFailureAlertsCommandControllerBackfillMutationError =
  void;

/**
 * @summary Backfill failed-check alerts (platform admin only)
 */
export const useBackfillCheckFailureAlertsCommandControllerBackfill = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof backfillCheckFailureAlertsCommandControllerBackfill>
      >,
      TError,
      { data?: BackfillCheckFailureAlertsDto },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<typeof backfillCheckFailureAlertsCommandControllerBackfill>
  >,
  TError,
  { data?: BackfillCheckFailureAlertsDto },
  TContext
> => {
  return useMutation(
    getBackfillCheckFailureAlertsCommandControllerBackfillMutationOptions(
      options,
    ),
    queryClient,
  );
};
